import deburr from 'lodash/deburr';
import isNil from 'lodash/isNil';
import kebabCase from 'lodash/kebabCase';

import { __t, applyTranslations } from 'utils/language';

import type {
  GeneralActionHandler,
  GeneralOnDelete,
  GeneralOnDuplicate,
  Icon,
  IconActionHandler,
  IconEnriched,
  Query,
  Report,
  ReportType
} from 'modules/insights/reports/types';

type PropValue = {
  [x: string]: string
};

applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));

const SHARED_MSG_KEY = 'insights_reports_shared';

export const ICONS_MAP = {
  view: 'eye-custom-fg',
  edit: 'pencil-custom-bg',
  duplicate: 'duplicate-custom-fg',
  delete: 'trash-custom-fg'
};

const getLink = (
  reportId?: number,
  reportType?: ReportType
): PropValue | undefined | null => (!isNil(reportId) && !isNil(reportType)
  ? { link: `/insights/reports/${reportType}/${String(reportId)}` }
  : null);

export const getIconView = (
  reportId?: number,
  reportType?: ReportType
): Partial<Icon> => ({
  ...getLink(reportId, reportType),
  name: ICONS_MAP.view,
  actionType: 'view',
  size: { width: 20, height: 20 },
  tooltip: __t(`${SHARED_MSG_KEY}.labels.view`)
});

export const getIconEdit = (): Icon => ({
  name: ICONS_MAP.edit,
  actionType: 'edit',
  size: { width: 18, height: 18 },
  tooltip: __t(`${SHARED_MSG_KEY}.labels.edit`)
});

export const getIconDuplicate = (
  onDuplicate: GeneralOnDuplicate
): Icon => ({
  name: ICONS_MAP.duplicate,
  actionType: 'duplicate',
  size: { width: 18, height: 18 },
  tooltip: __t(`${SHARED_MSG_KEY}.labels.duplicate`),
  onDuplicate
});

export const getIconDelete = (
  onDelete: GeneralOnDelete
): Icon => ({
  name: ICONS_MAP.delete,
  actionType: 'delete',
  size: { width: 18, height: 18 },
  tooltip: __t(`${SHARED_MSG_KEY}.labels.delete`),
  onDelete
});

const getActionHandler = (
  actionHandler: GeneralActionHandler,
  icon: Partial<Icon>
): IconActionHandler | undefined | null => (!isNil(icon[actionHandler])
  ? { [String(actionHandler)]: icon[actionHandler] }
  : null);

export const enrichReportIcon = (
  report: Report,
  icon: Partial<Icon>
): Partial<IconEnriched> => ({
  ...(icon.link ? { link: icon.link } : null),
  ...getActionHandler('onCreate', icon),
  ...getActionHandler('onDuplicate', icon),
  ...getActionHandler('onDelete', icon),
  id: `insights-reports-report-list__item-icon-${icon.name}-${String(report.id)}`,
  report,
  name: icon.name,
  actionType: icon.actionType,
  tooltip: icon.tooltip,
  size: icon.size
});

export const enrichQueryIcon = (
  query: Query,
  report: Report,
  icon: Icon
): IconEnriched => ({
  ...getActionHandler('onDuplicate', icon),
  ...getActionHandler('onDelete', icon),
  id: `insights-reports-query-list__item-icon-${icon.name}-${kebabCase(deburr(query.name))}`,
  report,
  query,
  name: icon.name,
  actionType: icon.actionType,
  tooltip: icon.tooltip,
  size: icon.size
});
